

import { useState } from "react";
import { useSettings } from "@/context/SettingsContext";
import { getImageUrl } from "@/lib/utils";
import { formatCurrencyFull, formatDate } from "@/lib/data";
import { formatDate as formatDateIndian } from "@/lib/dateFormat";
import { Printer } from "lucide-react";
import type { ClientPayment } from "./types";

interface PaymentReceiptProps {
  payment: ClientPayment;
  event: {
    id: number;
    name: string;
    start_date: string;
    venue?: { name: string } | null;
    client?: { name: string; phone?: string; email?: string } | null;
  };
  onClose: () => void;
}

// Indian number-to-words for receipt amount
function numberToWords(num: number): string {
  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
    "Sixteen", "Seventeen", "Eighteen", "Nineteen",
  ];
  const tens = [
    "", "", "Twenty", "Thirty", "Forty", "Fifty",
    "Sixty", "Seventy", "Eighty", "Ninety",
  ];

  function convertLessThanOneThousand(n: number): string {
    if (n === 0) return "";
    if (n < 20) return ones[n];
    const ten = Math.floor(n / 10);
    const remainder = n % 10;
    if (remainder === 0) return tens[ten];
    return `${tens[ten]} ${ones[remainder]}`;
  }

  function convert(n: number): string {
    if (n === 0) return "Zero";
    if (n < 0) return `Minus ${convert(-n)}`;
    const crore = Math.floor(n / 10000000);
    n %= 10000000;
    const lakh = Math.floor(n / 100000);
    n %= 100000;
    const thousand = Math.floor(n / 1000);
    n %= 1000;
    const hundred = Math.floor(n / 100);
    n %= 100;
    const parts: string[] = [];
    if (crore > 0) parts.push(`${convertLessThanOneThousand(crore)} Crore`);
    if (lakh > 0) parts.push(`${convertLessThanOneThousand(lakh)} Lakh`);
    if (thousand > 0) parts.push(`${convertLessThanOneThousand(thousand)} Thousand`);
    if (hundred > 0) parts.push(`${convertLessThanOneThousand(hundred)} Hundred`);
    if (n > 0) {
      if (parts.length > 0) parts.push("and");
      parts.push(convertLessThanOneThousand(n));
    }
    return parts.join(" ");
  }

  return `${convert(Math.floor(num))} Rupees${
    num % 1 !== 0 ? ` and ${Math.round((num % 1) * 100)} Paise` : ""
  } Only`;
}

// Professional printable receipt — uses global print-* CSS classes from globals.css
function PrintableReceipt({
  payment,
  event,
  business,
  logoUrl,
}: {
  payment: ClientPayment;
  event: PaymentReceiptProps["event"];
  business: any;
  logoUrl: string;
}) {
  const receiptNumber = `RCP-${String(payment.id).padStart(5, "0")}`;
  const amountWords = numberToWords(Number(payment.amount));

  return (
    <div id="print-receipt-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Gold accent bar */}
        <div className="print-header-accent" />

        {/* Business header — Option A: Logo · Name+Tagline · Number box */}
        <div className="print-header">
          <div style={{ display: "flex", alignItems: "center", gap: "5mm", marginBottom: "3mm" }}>
            {/* Logo */}
            {logoUrl && (
              <div style={{ flexShrink: 0 }}>
                <img
                  src={logoUrl}
                  alt={business.businessName}
                  className="print-logo"
                  style={{ height: "55px", maxWidth: "130px", objectFit: "contain", display: "block" }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              </div>
            )}

            {/* Business name + tagline — centered flex-grow */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <p style={{ fontSize: "18pt", fontWeight: 800, color: "#111827", letterSpacing: "-0.3px", lineHeight: 1.1, marginBottom: "1.5mm" }}>
                {(business.businessName || "Shalimar").toUpperCase()}
              </p>
              <p style={{ fontSize: "10pt", color: "#C9A84C", fontWeight: 600 }}>
                {business.tagline || "BANQUET HALL & EVENT SERVICES"}
              </p>
            </div>

            {/* Receipt number box */}
            <div className="print-invoice-number" style={{ flexShrink: 0, padding: "1mm 2mm", textAlign: "right" }}>
              <p style={{ fontSize: "6pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#6b7280", marginBottom: "0.5mm" }}>
                Receipt No.
              </p>
              <p style={{ fontSize: "9pt", fontWeight: 800, color: "#C9A84C", lineHeight: 1.1 }}>
                {receiptNumber}
              </p>
              <p style={{ fontSize: "6pt", color: "#6b7280", marginTop: "0.5mm" }}>
                <span style={{ fontWeight: 600 }}>Event #:</span> {event.id}
              </p>
            </div>
          </div>

          {/* Contact info — address line, then contact details line */}
          <div style={{ textAlign: "center", fontSize: "8pt", color: "#6b7280", lineHeight: 1.6, paddingBottom: "2mm" }}>
            {business.address && (
              <p>{business.address}</p>
            )}
            <p>
              {[
                business.phone ? `Phone: ${business.phone}` : null,
                business.email ? `Email: ${business.email}` : null,
                business.gstNumber ? `GSTIN: ${business.gstNumber}` : null,
              ].filter(Boolean).join("  •  ")}
            </p>
          </div>
        </div>

        <div className="print-divider" />

        {/* Info cards — inline flex for reliable print justification */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            marginBottom: "6mm",
            gap: "4mm",
          }}
        >
          {[
            {
              label: "Received From",
              value: event.client?.name || "—",
              sub: [event.client?.phone, event.client?.email].filter(Boolean),
            },
            {
              label: "Event",
              value: event.name,
              sub: [event.venue?.name, formatDate(event.start_date)].filter(Boolean),
            },
            {
              label: "Payment Date",
              value: formatDate(payment.payment_date),
              sub: [],
            },
            {
              label: "Payment Method",
              value: payment.payment_method.replace(/_/g, " "),
              sub: [],
              capitalize: true,
            },
          ].map((card, i) => (
            <div
              key={i}
              style={{
                flex: "1 1 0",
                textAlign: "center",
                padding: "2mm 1mm",
                borderLeft: i > 0 ? "1px solid #e5e7eb" : "none",
              }}
            >
              <p className="print-info-card-label">{card.label}</p>
              <p
                className="print-info-card-value"
                style={card.capitalize ? { textTransform: "capitalize" } : {}}
              >
                {card.value}
              </p>
              {card.sub.map((s, j) => (
                <p key={j} style={{ fontSize: "8pt", color: "#6b7280", marginTop: "0.5mm" }}>
                  {s}
                </p>
              ))}
            </div>
          ))}
        </div>

        {/* Amount received highlight box */}
        <div className="print-payment-status mb-6">
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontWeight: 700, fontSize: "7.5pt", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "1mm" }}>
              Amount Received
            </p>
            <p style={{ fontSize: "7.5pt", color: "#374151", fontStyle: "italic", lineHeight: 1.4 }}>
              {amountWords}
            </p>
          </div>
          <div style={{ flexShrink: 0, textAlign: "right", paddingLeft: "4mm" }}>
            <p style={{ fontSize: "18pt", fontWeight: 800, color: "#C9A84C", lineHeight: 1, whiteSpace: "nowrap" }}>
              {formatCurrencyFull(payment.amount)}
            </p>
          </div>
        </div>

        {/* Payment details table */}
        <div className="mb-6">
          <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
            <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">
              Payment Details
            </h3>
          </div>
          <table className="print-table">
            <tbody>
              <tr>
                <td className="font-semibold text-stone-600 w-1/2">Receipt Number</td>
                <td className="text-right font-bold text-stone-900">
                  {receiptNumber}
                </td>
              </tr>
              <tr>
                <td className="font-semibold text-stone-600">Payment Method</td>
                <td className="text-right font-bold text-stone-900 capitalize">
                  {payment.payment_method.replace(/_/g, " ")}
                </td>
              </tr>
              <tr>
                <td className="font-semibold text-stone-600">Payment Date</td>
                <td className="text-right font-bold text-stone-900">
                  {formatDate(payment.payment_date)}
                </td>
              </tr>
              {payment.transaction_id && (
                <tr>
                  <td className="font-semibold text-stone-600">
                    Reference / Transaction ID
                  </td>
                  <td
                    className="text-right text-stone-900"
                    style={{ fontFamily: "monospace", fontSize: "8pt" }}
                  >
                    {payment.transaction_id}
                  </td>
                </tr>
              )}
              {payment.notes && (
                <tr>
                  <td className="font-semibold text-stone-600">Remarks</td>
                  <td
                    className="text-right text-stone-700"
                    style={{ fontSize: "8pt" }}
                  >
                    {payment.notes}
                  </td>
                </tr>
              )}
              <tr className="print-total-row">
                <td className="font-bold">TOTAL AMOUNT RECEIVED</td>
                <td className="text-right font-bold">
                  {formatCurrencyFull(payment.amount)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Signature / stamp area */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "8mm",
            marginBottom: "18mm",
          }}
        >
          <div style={{ fontSize: "8pt", color: "#6b7280", flex: 1 }}>
            <p style={{ marginBottom: "1mm", fontWeight: 500 }}>
              This is a computer-generated receipt and is valid without a physical
              signature.
            </p>
            <p>
              For queries:{" "}
              {business.phone
                ? business.phone
                : business.email
                ? business.email
                : "—"}
            </p>
          </div>
          <div style={{ textAlign: "center", flexShrink: 0 }}>
            <div
              style={{
                width: "80px",
                height: "80px",
                border: "2px dashed #9ca3af",
                borderRadius: "4px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "2px",
              }}
            >
              <span
                style={{
                  fontSize: "7pt",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  letterSpacing: "1px",
                }}
              >
                Official
              </span>
              <span
                style={{
                  fontSize: "7pt",
                  color: "#9ca3af",
                  textTransform: "uppercase",
                  fontWeight: 700,
                  letterSpacing: "1px",
                }}
              >
                Stamp
              </span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">
              Thank you for choosing {business.businessName || "Shalimar"}!
            </p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span>{" "}
              {business.email || "info@shalimar.com"} |{" "}
              {business.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">
              {formatDateIndian(new Date())}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReceiptButton({
  payment,
  event,
}: {
  payment: ClientPayment;
  event: PaymentReceiptProps["event"];
}) {
  const { settings } = useSettings();
  const [printing, setPrinting] = useState(false);

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrinting(false), 200);
    }, 80);
  };

  return (
    <>
      <button
        onClick={handlePrint}
        className="inline-flex items-center gap-1 text-[10px] md:text-xs text-blue-600 hover:text-blue-700 font-medium transition-colors"
        title="Print Receipt"
      >
        <Printer className="w-3 h-3" />
        Receipt
      </button>

      {printing && (
        <PrintableReceipt
          payment={payment}
          event={event}
          business={settings.business}
          logoUrl={settings.logoUrl ? getImageUrl(settings.logoUrl) : ""}
        />
      )}
    </>
  );
}

export default ReceiptButton;

export function ReceiptPrintStyles() {
  return (
    <style>{`
      @media print {
        @page {
          size: A4;
          margin: 0;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        html, body {
          margin: 0;
          padding: 0;
          background: white;
          width: 100%;
          height: 100%;
        }

        body {
          font-family: system-ui, -apple-system, sans-serif;
          color: #000;
          background: white;
        }

        img {
          max-width: 100%;
          height: auto;
          display: block;
        }

        table {
          page-break-inside: avoid;
        }

        tr {
          page-break-inside: avoid;
        }

        p, span, td, th {
          page-break-inside: avoid;
        }
      }
    `}</style>
  );
}
